import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ArrowLeft, Calculator, Building2, ArrowRightLeft, DollarSign,
  Calendar, CheckCircle, AlertTriangle, Clock
} from "lucide-react";
import { ChatToggleButton } from "@/components/AIChatWidget";
import axios from "axios";
import { toast } from "sonner";

const Exchange1031 = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [calculating, setCalculating] = useState(false);
  const [result, setResult] = useState(null);
  
  const [formData, setFormData] = useState({
    relinquished_property_name: "",
    relinquished_sale_price: 500000,
    relinquished_original_basis: 350000,
    relinquished_improvements: 50000,
    relinquished_depreciation_taken: 75000,
    relinquished_selling_costs: 30000,
    relinquished_mortgage_balance: 200000,
    replacement_property_name: "",
    replacement_purchase_price: 600000,
    replacement_closing_costs: 12000,
    replacement_mortgage: 300000,
    capital_gains_rate: 15,
    depreciation_recapture_rate: 25,
    state_tax_rate: 5,
    identification_deadline_days: 45,
    closing_deadline_days: 180
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setResult(null);
  };

  const calculate = async () => {
    if (!formData.relinquished_property_name) {
      toast.error("Please enter the property name you're selling");
      return;
    }

    setCalculating(true);
    try {
      const response = await axios.post(`${API}/calculator/1031-exchange`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResult(response.data.results);
      toast.success("1031 Exchange calculated!");
    } catch (error) {
      toast.error("Failed to calculate");
    } finally {
      setCalculating(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  return (
    <div className="min-h-screen bg-slate-900" data-testid="1031-exchange-page">
      {/* Header */}
      <header className="glass border-b border-slate-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate("/dashboard")}
                className="text-slate-400 hover:text-white"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Dashboard
              </Button>
              <div className="h-6 w-px bg-slate-700"></div>
              <h1 className="text-lg font-semibold text-white font-['Manrope']">1031 Exchange Calculator</h1>
            </div>
            <div className="flex items-center gap-3">
              <ChatToggleButton />
              <Button 
              onClick={calculate}
              disabled={calculating}
              className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
              data-testid="calculate-1031-btn"
            >
              {calculating ? (
                <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-slate-900 mr-2"></div>
              ) : (
                <Calculator className="h-4 w-4 mr-2" />
              )}
              Calculate
            </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Input Form */}
          <div className="space-y-6">
            {/* Relinquished Property */}
            <div className="glass-card rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Building2 className="h-5 w-5 text-red-500" />
                Property You're Selling (Relinquished)
              </h3>
              <div className="space-y-4">
                <div>
                  <Label className="text-slate-300">Property Name *</Label>
                  <Input
                    value={formData.relinquished_property_name}
                    onChange={(e) => handleInputChange('relinquished_property_name', e.target.value)}
                    placeholder="123 Old Property Lane"
                    className="bg-slate-950 border-slate-800 text-white"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Sale Price</Label>
                    <Input
                      type="number"
                      value={formData.relinquished_sale_price}
                      onChange={(e) => handleInputChange('relinquished_sale_price', parseFloat(e.target.value) || 0)}
                      className="bg-slate-950 border-slate-800 text-white font-mono"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Selling Costs</Label>
                    <Input
                      type="number"
                      value={formData.relinquished_selling_costs}
                      onChange={(e) => handleInputChange('relinquished_selling_costs', parseFloat(e.target.value) || 0)}
                      className="bg-slate-950 border-slate-800 text-white font-mono"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Original Purchase Price</Label>
                    <Input
                      type="number"
                      value={formData.relinquished_original_basis}
                      onChange={(e) => handleInputChange('relinquished_original_basis', parseFloat(e.target.value) || 0)}
                      className="bg-slate-950 border-slate-800 text-white font-mono"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Capital Improvements</Label>
                    <Input
                      type="number"
                      value={formData.relinquished_improvements}
                      onChange={(e) => handleInputChange('relinquished_improvements', parseFloat(e.target.value) || 0)}
                      className="bg-slate-950 border-slate-800 text-white font-mono"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Depreciation Taken</Label>
                    <Input
                      type="number"
                      value={formData.relinquished_depreciation_taken}
                      onChange={(e) => handleInputChange('relinquished_depreciation_taken', parseFloat(e.target.value) || 0)}
                      className="bg-slate-950 border-slate-800 text-white font-mono"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Mortgage Balance</Label>
                    <Input
                      type="number"
                      value={formData.relinquished_mortgage_balance}
                      onChange={(e) => handleInputChange('relinquished_mortgage_balance', parseFloat(e.target.value) || 0)}
                      className="bg-slate-950 border-slate-800 text-white font-mono"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Replacement Property */}
            <div className="glass-card rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Building2 className="h-5 w-5 text-emerald-500" />
                Property You're Buying (Replacement)
              </h3>
              <div className="space-y-4">
                <div>
                  <Label className="text-slate-300">Property Name</Label>
                  <Input
                    value={formData.replacement_property_name}
                    onChange={(e) => handleInputChange('replacement_property_name', e.target.value)}
                    placeholder="456 New Property Ave"
                    className="bg-slate-950 border-slate-800 text-white"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Purchase Price</Label>
                    <Input
                      type="number"
                      value={formData.replacement_purchase_price}
                      onChange={(e) => handleInputChange('replacement_purchase_price', parseFloat(e.target.value) || 0)}
                      className="bg-slate-950 border-slate-800 text-white font-mono"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Closing Costs</Label>
                    <Input
                      type="number"
                      value={formData.replacement_closing_costs}
                      onChange={(e) => handleInputChange('replacement_closing_costs', parseFloat(e.target.value) || 0)}
                      className="bg-slate-950 border-slate-800 text-white font-mono"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-slate-300">New Mortgage</Label>
                  <Input
                    type="number"
                    value={formData.replacement_mortgage}
                    onChange={(e) => handleInputChange('replacement_mortgage', parseFloat(e.target.value) || 0)}
                    className="bg-slate-950 border-slate-800 text-white font-mono"
                  />
                </div>
              </div>
            </div>

            {/* Tax Rates */}
            <div className="glass-card rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-amber-500" />
                Tax Rates
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-slate-300">Capital Gains %</Label>
                  <Input
                    type="number"
                    value={formData.capital_gains_rate}
                    onChange={(e) => handleInputChange('capital_gains_rate', parseFloat(e.target.value) || 0)}
                    className="bg-slate-950 border-slate-800 text-white font-mono"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Depreciation Recapture %</Label>
                  <Input
                    type="number"
                    value={formData.depreciation_recapture_rate}
                    onChange={(e) => handleInputChange('depreciation_recapture_rate', parseFloat(e.target.value) || 0)}
                    className="bg-slate-950 border-slate-800 text-white font-mono"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">State Tax %</Label>
                  <Input
                    type="number"
                    value={formData.state_tax_rate}
                    onChange={(e) => handleInputChange('state_tax_rate', parseFloat(e.target.value) || 0)}
                    className="bg-slate-950 border-slate-800 text-white font-mono"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Results */}
          <div className="space-y-6">
            {!result ? (
              <div className="glass-card rounded-xl p-12 text-center h-full flex flex-col items-center justify-center">
                <ArrowRightLeft className="h-12 w-12 text-slate-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">1031 Exchange Analysis</h3>
                <p className="text-slate-400 mb-6">Calculate tax deferral benefits of a like-kind exchange</p>
                <div className="text-left text-sm text-slate-500 max-w-sm">
                  <p className="mb-2"><strong className="text-slate-400">Key Benefits:</strong></p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Defer capital gains taxes</li>
                    <li>Defer depreciation recapture</li>
                    <li>Leverage into larger properties</li>
                    <li>Build wealth tax-efficiently</li>
                  </ul>
                </div>
              </div>
            ) : (
              <>
                {/* Qualification Status */}
                <div className={`rounded-xl p-6 ${result.exchange_summary.is_fully_qualified ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-amber-500/10 border border-amber-500/30'}`}>
                  <div className="flex items-center gap-3 mb-2">
                    {result.exchange_summary.is_fully_qualified ? (
                      <CheckCircle className="h-6 w-6 text-emerald-500" />
                    ) : (
                      <AlertTriangle className="h-6 w-6 text-amber-500" />
                    )}
                    <span className="text-lg font-semibold text-white">
                      {result.exchange_summary.qualification_status}
                    </span>
                  </div>
                  <div className="text-3xl font-bold font-mono text-emerald-500" data-testid="tax-deferred">
                    {formatCurrency(result.exchange_summary.tax_deferred)} Tax Deferred
                  </div>
                </div>

                {/* Deadlines */}
                <div className="glass-card rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <Clock className="h-5 w-5 text-cyan-500" />
                    Critical Deadlines
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-slate-950/50 rounded-lg p-4 text-center">
                      <div className="text-3xl font-bold text-amber-500">{result.exchange_requirements.identification_deadline_days}</div>
                      <div className="text-sm text-slate-400">Days to Identify</div>
                    </div>
                    <div className="bg-slate-950/50 rounded-lg p-4 text-center">
                      <div className="text-3xl font-bold text-cyan-500">{result.exchange_requirements.closing_deadline_days}</div>
                      <div className="text-sm text-slate-400">Days to Close</div>
                    </div>
                  </div>
                </div>

                {/* Gain Analysis */}
                <div className="glass-card rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-white mb-4">Gain Analysis</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between py-2 border-b border-slate-800">
                      <span className="text-slate-400">Sale Price</span>
                      <span className="font-mono text-white">{formatCurrency(result.relinquished_property.sale_price)}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-slate-800">
                      <span className="text-slate-400">Adjusted Basis</span>
                      <span className="font-mono text-white">{formatCurrency(result.relinquished_property.adjusted_basis)}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-slate-800 font-semibold">
                      <span className="text-slate-300">Total Gain</span>
                      <span className="font-mono text-amber-500">{formatCurrency(result.gain_analysis.total_gain)}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-slate-800 pl-4">
                      <span className="text-slate-500">Capital Gain</span>
                      <span className="font-mono text-slate-400">{formatCurrency(result.gain_analysis.capital_gain)}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-slate-800 pl-4">
                      <span className="text-slate-500">Depreciation Recapture</span>
                      <span className="font-mono text-slate-400">{formatCurrency(result.gain_analysis.depreciation_recapture)}</span>
                    </div>
                  </div>
                </div>

                {/* Tax Comparison */}
                <div className="glass-card rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-white mb-4">Tax Without Exchange</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between py-2 border-b border-slate-800">
                      <span className="text-slate-400">Federal Capital Gains Tax</span>
                      <span className="font-mono text-red-400">{formatCurrency(result.tax_without_exchange.federal_capital_gains_tax)}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-slate-800">
                      <span className="text-slate-400">Depreciation Recapture Tax</span>
                      <span className="font-mono text-red-400">{formatCurrency(result.tax_without_exchange.depreciation_recapture_tax)}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-slate-800">
                      <span className="text-slate-400">State Tax</span>
                      <span className="font-mono text-red-400">{formatCurrency(result.tax_without_exchange.state_tax)}</span>
                    </div>
                    <div className="flex justify-between py-2 font-semibold">
                      <span className="text-slate-300">Total Tax Due</span>
                      <span className="font-mono text-red-500">{formatCurrency(result.tax_without_exchange.total_tax)}</span>
                    </div>
                  </div>
                </div>

                {/* Boot Analysis */}
                {result.boot_analysis.total_boot > 0 && (
                  <div className="glass-card rounded-xl p-6 border-l-4 border-amber-500">
                    <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5 text-amber-500" />
                      Boot (Taxable Portion)
                    </h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-slate-400">Cash Boot</span>
                        <span className="font-mono text-white">{formatCurrency(result.boot_analysis.cash_boot)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Mortgage Boot</span>
                        <span className="font-mono text-white">{formatCurrency(result.boot_analysis.mortgage_boot)}</span>
                      </div>
                      <div className="flex justify-between font-semibold pt-2 border-t border-slate-800">
                        <span className="text-slate-300">Tax on Boot</span>
                        <span className="font-mono text-amber-500">{formatCurrency(result.boot_analysis.boot_tax)}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Requirements */}
                <div className="glass-card rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-white mb-4">Exchange Requirements</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between py-2 border-b border-slate-800">
                      <span className="text-slate-400">Minimum Replacement Price</span>
                      <span className="font-mono text-cyan-500">{formatCurrency(result.exchange_requirements.min_replacement_price)}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-slate-800">
                      <span className="text-slate-400">Minimum Equity to Reinvest</span>
                      <span className="font-mono text-cyan-500">{formatCurrency(result.exchange_requirements.min_equity_to_reinvest)}</span>
                    </div>
                    <div className="flex justify-between py-2">
                      <span className="text-slate-400">New Basis in Replacement</span>
                      <span className="font-mono text-white">{formatCurrency(result.replacement_property.new_basis)}</span>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Exchange1031;
